import { TextInput, Text, Grid, Select, Flex } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useUserData } from 'hooks/api/useUser';
import { useUpdatePersonalData } from 'hooks/api/useProfile';
import { displayNotificationSuccess } from 'utils/notifications';
import { LanguageCode, completeCountryList } from 'utils/i18n';
import i18next from 'i18next';
import { DatePickerInput } from '@mantine/dates';
import { PortalButton } from 'components/Button/PortalButton';
import { BsFloppy } from 'react-icons/bs';
import { queryClient, queryKeys } from 'utils/query';
import { validateEmail, validatePhone, validateRequired } from 'utils/validation';
import { Gender } from '@extrahorizon/javascript-sdk';
import { GenderMap } from 'models/profile';
import { HourFormat, UnitType } from 'models/generic';
import { getUserMeasurementSystem, getUserPhysicianIdentifier } from 'utils/helpers';
import { PaperWithBorder } from 'components/PaperWithBorder';
import { generalDateFormat, getUserHourFormat } from 'utils/dates';
import { showUsaFeature } from 'utils/app';

export const GeneralForm = () => {
  const { t } = useTranslation();
  const user = useUserData();
  const userData = user.data?.user;
  const userProfileData = user.data?.profile;
  const userId = userData?.id || '';
  const [isLoading, setLoading] = useState(false);
  const { language } = i18next;
  const countryList = useMemo(() => completeCountryList(language), [language]);

  const form = useForm({
    initialValues: {
      firstName: userData?.firstName,
      lastName: userData?.lastName,
      email: userData?.email,
      phoneNumber: userData?.phoneNumber,
      birthDate: userProfileData?.birthday ? new Date(userProfileData?.birthday) : undefined,
      gender: (userProfileData?.gender || Gender.Unknown).toString(),
      country: userProfileData?.country || '',
      // The language is done this was since we want to save in "English", even if the dropdown in not present
      language: language && language === LanguageCode.American_English ? LanguageCode.English : language,
      measurementSystem: getUserMeasurementSystem(user.data),
      hourFormat: getUserHourFormat(user.data),
      physicianIdentifier: getUserPhysicianIdentifier(user.data),
    },

    validate: {
      firstName: (value) => validateRequired({ value, label: t('first_name_label') }),
      lastName: (value) => validateRequired({ value, label: t('last_name_label') }),
      email: (value) => validateEmail(value),
      phoneNumber: (value) => validatePhone(value),
    },
  });

  const updatePersonalDataSuccess = async () => {
    form.resetDirty();
    await queryClient.invalidateQueries({ queryKey: [queryKeys.userData] });
    const savedLanguage = form.values.language;
    displayNotificationSuccess({
      title: i18next.t('success_label', { lng: savedLanguage }),
      message: i18next.t('success_data_updated', {
        value: i18next.t('personal_data_label', { lng: savedLanguage }),
        lng: savedLanguage,
      }),
    });
    setLoading(false);
  };

  const { mutate: updatePersonalData } = useUpdatePersonalData({
    values: {
      id: userId,
      firstName: form.values.firstName,
      lastName: form.values.lastName,
      phoneNumber: form.values.phoneNumber,
      language: form.values.language,
      email: form.values.email,
      country: form.values.country || 'BE',
      birthday: dayjs(form.values.birthDate).format('YYYY-MM-DD'), // specific format for profile service
      gender: Number(form.values.gender),
      measurementSystem: form.values.measurementSystem?.toLocaleLowerCase(),
      hourFormat: form.values.hourFormat,
      physicianIdentifier: form.values.physicianIdentifier,
    },
    callback: updatePersonalDataSuccess,
  });

  const saveGeneralInfo = async () => {
    setLoading(true);
    updatePersonalData();
  };

  return (
    <PaperWithBorder title={t('group_general_tab')}>
      <form onSubmit={form.onSubmit(saveGeneralInfo)}>
        <Grid align={'center'} mt={'md'}>
          <Grid.Col span={5}>
            <Text>{t('first_name_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <TextInput placeholder={t('first_name_label')} {...form.getInputProps('firstName')} />
          </Grid.Col>
          <Grid.Col span={5}>
            <Text>{t('last_name_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <TextInput placeholder={t('last_name_label')} {...form.getInputProps('lastName')} />
          </Grid.Col>
          <Grid.Col span={5}>
            <Text>{t('email_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <TextInput placeholder={t('email_label')} {...form.getInputProps('email')} />
          </Grid.Col>
          <Grid.Col span={5}>
            <Text>{t('phone_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <TextInput required placeholder={t('phone_label')} {...form.getInputProps('phoneNumber')} />
          </Grid.Col>
          <Grid.Col span={5}>
            <Text>{t('date_of_birth_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <DatePickerInput
              minDate={dayjs(new Date()).subtract(125, 'year').toDate()}
              maxDate={dayjs(new Date()).subtract(18, 'year').toDate()}
              valueFormat={generalDateFormat}
              {...form.getInputProps('birthDate')}
            />
          </Grid.Col>
          <Grid.Col span={5}>
            <Text>{t('gender_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <Select
              required
              placeholder={t('gender_label')}
              data={GenderMap.map((gender) => ({
                value: gender.code.toString(),
                label: t(`${gender.label}`),
              }))}
              {...form.getInputProps('gender')}
            />
          </Grid.Col>
          <Grid.Col span={5}>
            <Text>{t('group_country_label')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <Select
              required
              data={countryList}
              placeholder={t('group_country_label')}
              {...form.getInputProps('country')}
              searchable
            />
          </Grid.Col>

          {!showUsaFeature && (
            <>
              <Grid.Col span={5}>
                <Text>{t('group_language_label')}</Text>
              </Grid.Col>
              <Grid.Col span={7}>
                <Select
                  required
                  data={Object.values(LanguageCode)
                    .filter((lang) => lang !== LanguageCode.American_English)
                    .map((lang) => ({
                      value: lang,
                      label: t(`lang_${lang}`),
                    }))}
                  placeholder={t('group_language_label')}
                  {...form.getInputProps('language')}
                />
              </Grid.Col>
            </>
          )}

          <Grid.Col span={5}>
            <Text>{t('label_unit_system')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <Select
              required
              data={Object.values(UnitType).map((units) => ({
                value: units,
                label: units,
              }))}
              placeholder={t('label_unit_system')}
              {...form.getInputProps('measurementSystem')}
            />
          </Grid.Col>
          <Grid.Col span={5}>
            <Text>{t('label_hour_format')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <Select
              required
              data={Object.values(HourFormat).map((hourFormat) => ({
                value: hourFormat,
                label: `${hourFormat.substring(0, 2)} ${t('hours_label')}`,
              }))}
              placeholder={t('label_hour_format')}
              {...form.getInputProps('hourFormat')}
            />
          </Grid.Col>
          <Grid.Col span={5}>
            <Text>{t('label_physician_identifier')}</Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <TextInput placeholder={t('label_physician_identifier')} {...form.getInputProps('physicianIdentifier')} />
          </Grid.Col>
        </Grid>
        <Flex justify={'flex-start'} mt={20}>
          <PortalButton
            type={'submit'}
            label={t('save_label')}
            Icon={BsFloppy}
            loading={isLoading}
            disabled={!form.isDirty()}
          />
        </Flex>
      </form>
    </PaperWithBorder>
  );
};
